<div class="management-page">
    <app-breadcrumbs [model]="libraryBreadcrumbs()" />
    <header class="overview-header">
        <div>
            <p class="overview-eyebrow">{{ t()('management.eyebrow') }}</p>
            <h1>{{ t()('management.firmware') }}</h1>
            <p class="overview-description">{{ t()('management.firmwareDescription') }}</p>
        </div>
        <div class="overview-actions">
            <a pButton [outlined]="true" routerLink="/packages">
                <i pButtonIcon class="pi pi-box" aria-hidden="true"></i>
                <span pButtonLabel>{{ t()('management.packages') }}</span>
            </a>
            <p-button icon="pi pi-refresh" [outlined]="true" [ariaLabel]="t()('management.refresh')" [pTooltip]="t()('management.refresh')" [disabled]="loading()" (onClick)="loadFirmware()" />
            @if (canManageFirmware) {
                <p-button [label]="t()('management.uploadFirmware')" icon="pi pi-upload" (onClick)="showUploadDialog.set(true)" />
            }
        </div>
    </header>
    <div class="management-summary">
        <div class="management-stat"><span><i class="pi pi-microchip" aria-hidden="true"></i>{{ t()('management.firmwareFiles') }}</span><strong>{{ loading() || loadError() ? '—' : firmware().length }}</strong></div>
        <div class="management-stat"><span><i class="pi pi-desktop" aria-hidden="true"></i>{{ t()('management.compatibleModels') }}</span><strong>{{ loading() || loadError() ? '—' : allModels().length }}</strong></div>
        <div class="management-stat"><span><i class="pi pi-database" aria-hidden="true"></i>{{ t()('management.storage') }}</span><strong>{{ loading() || loadError() ? '—' : formatFileSize(totalSize()) }}</strong></div>
    </div>
    <section class="management-panel" [attr.aria-label]="t()('management.firmware')">
        <div class="management-toolbar">
            <label class="management-search">
                <i class="pi pi-search" aria-hidden="true"></i>
                <input pInputText type="search" [attr.aria-label]="t()('management.searchFirmware')" [placeholder]="t()('management.searchFirmware')" [ngModel]="search()" (ngModelChange)="search.set($event || '')" />
            </label>
            <p-autocomplete [ngModel]="filterModel()" (ngModelChange)="filterModel.set($event || '')" (onClear)="resetFilter()" [suggestions]="modelSuggestions()" (completeMethod)="searchModels($event)"
                [placeholder]="t()('management.filterModel')" [ariaLabel]="t()('management.filterModel')" [showClear]="true" [minQueryLength]="0" appendTo="body" class="firmware-model-filter" />
            <p-select [options]="typeOptions()" optionLabel="label" optionValue="value" [ngModel]="typeFilter()" (ngModelChange)="typeFilter.set($event)" [ariaLabel]="t()('management.fileType')" appendTo="body" />
            @if (hasFilters()) { <p-button [label]="t()('management.clearFilters')" icon="pi pi-filter-slash" [text]="true" (onClick)="clearFilters()" /> }
            <span class="management-results">{{ loading() || loadError() ? '—' : filteredFirmware().length }} / {{ loading() || loadError() ? '—' : firmware().length }} {{ t()('management.shown') }}</span>
        </div>
    @if (loading()) {
        <div class="management-state"><app-loading-skeleton preset="table" [count]="5" [label]="t()('management.loadingFirmware')" /></div>
    } @else if (loadError()) {
        <div class="management-state"><app-empty-state icon="pi pi-exclamation-circle" tone="error" [title]="t()('management.firmwareError')" [description]="t()('management.retryHint')" /></div>
    } @else if (filteredFirmware().length === 0) {
        <div class="management-state"><app-empty-state icon="pi pi-microchip" [title]="t()(hasFilters() ? 'management.noMatches' : 'management.noFirmware')" [description]="t()(hasFilters() ? 'management.clearHint' : 'management.emptyFirmwareHint')" /></div>
    } @else {
    <p-table
        [value]="filteredFirmware()"
        [paginator]="true"
        [rows]="15"
        [rowsPerPageOptions]="[15, 25, 50]"
        [first]="tableFirst()"
        (onPage)="tableFirst.set($event.first)"
        [rowHover]="true"
        [scrollable]="true"
        class="management-table"
        [sortField]="'name'"
        [sortOrder]="1">
        <ng-template #header>
            <tr>
                <th pSortableColumn="name">{{ t()('management.name') }} <p-sorticon field="name" /></th>
                <th>{{ t()('management.fileType') }}</th>
                <th pSortableColumn="version">{{ t()('management.version') }} <p-sorticon field="version" /></th>
                <th>{{ t()('management.compatibleModels') }}</th>
                <th pSortableColumn="creationDate">{{ t()('management.created') }} <p-sorticon field="creationDate" /></th>
                <th pSortableColumn="fileSizeBytes">{{ t()('management.fileSize') }} <p-sorticon field="fileSizeBytes" /></th>
                <th pSortableColumn="uploadedUtc">{{ t()('management.uploaded') }} <p-sorticon field="uploadedUtc" /></th>
                <th class="text-right">{{ t()('management.actions') }}</th>
            </tr>
        </ng-template>
        <ng-template #body let-fw>
            <tr>
                <td>
                    <div class="management-name"><i class="pi pi-microchip" aria-hidden="true"></i><div><button type="button" (click)="viewFirmware(fw)">{{ fw.name }}</button><small>{{ fw.originalFileName }}</small></div></div>
                </td>
                <td><p-tag [value]="(fw.firmwareType || 'puf').toUpperCase()" [severity]="fw.firmwareType === 'zip' ? 'warn' : 'success'" [style]="{fontSize: '0.7rem'}"></p-tag></td>
                <td><p-tag [value]="fw.version" severity="info"></p-tag></td>
                <td>
                    <div class="flex flex-wrap gap-1">
                        @for (model of fw.compatibleModels?.slice(0, 3); track model) {
                        <p-tag [value]="model" severity="secondary" [style]="{fontSize: '0.7rem'}"></p-tag>
                        } @if (fw.compatibleModels?.length > 3) {
                        <p-tag [value]="'+' + (fw.compatibleModels.length - 3) + ' more'" severity="contrast" [style]="{fontSize: '0.7rem'}"></p-tag>
                        }
                    </div>
                </td>
                <td>{{ formatDateOnly(fw.creationDate) }}</td>
                <td>{{ formatFileSize(fw.fileSizeBytes) }}</td>
                <td>{{ formatDate(fw.uploadedUtc) }}</td>
                <td>
                    <div class="management-row-actions">
                        <p-button icon="pi pi-eye" [text]="true" [rounded]="true" (onClick)="viewFirmware(fw)" [ariaLabel]="t()('management.viewDetails') + ' ' + fw.name" [pTooltip]="t()('management.viewDetails')" />
                        <p-button icon="pi pi-download" [text]="true" [rounded]="true" (onClick)="downloadFirmware(fw)" [ariaLabel]="t()('management.download') + ' ' + fw.name" [pTooltip]="t()('management.download')" />
                        @if (canManageFirmware) {
                        <p-button icon="pi pi-trash" [text]="true" [rounded]="true" severity="danger" (onClick)="confirmDelete(fw)" [ariaLabel]="t()('management.delete') + ' ' + fw.name" [pTooltip]="t()('management.delete')" />
                        }
                    </div>
                </td>
            </tr>
        </ng-template>
    </p-table>
    }
    </section>
</div>

<!-- Firmware Details Dialog -->
<p-dialog header="Firmware Details" [(visible)]="showDetailDialog" [modal]="true" [draggable]="false" [resizable]="false" [style]="{width: '700px', maxWidth: '95vw'}">
    @if (selectedFirmware(); as fw) {
    <div class="flex flex-col gap-4 p-2">
        <!-- Header info -->
        <div class="flex items-start justify-between">
            <div>
                <h3 class="m-0">{{ fw.name }}</h3>
                @if (fw.description && fw.description !== fw.name) {
                <p class="text-color-secondary mt-1">{{ fw.description }}</p>
                }
            </div>
            @if (canManageFirmware) {
            <p-button icon="pi pi-pencil" [text]="true" [rounded]="true" size="small" (onClick)="openRename(fw)" pTooltip="Rename"></p-button>
            }
        </div>

        <!-- Version and metadata -->
        <div class="grid grid-cols-2 gap-3">
            <div>
                <span class="text-sm text-color-secondary">Version</span>
                <div class="font-medium">{{ fw.version }}</div>
            </div>
            @if (fw.creationDate) {
            <div>
                <span class="text-sm text-color-secondary">Creation Date</span>
                <div class="font-medium">{{ fw.creationDate }}</div>
            </div>
            } @if (fw.creationAuthor) {
            <div>
                <span class="text-sm text-color-secondary">Author</span>
                <div class="font-medium">{{ fw.creationAuthor }}</div>
            </div>
            } @if (fw.creationOrganization) {
            <div>
                <span class="text-sm text-color-secondary">Organization</span>
                <div class="font-medium">{{ fw.creationOrganization }}</div>
            </div>
            }
            <div>
                <span class="text-sm text-color-secondary">File Size</span>
                <div class="font-medium">{{ formatFileSize(fw.fileSizeBytes) }}</div>
            </div>
            <div>
                <span class="text-sm text-color-secondary">Uploaded</span>
                <div class="font-medium">{{ formatDate(fw.uploadedUtc) }}</div>
            </div>
        </div>

        <!-- Version requirements -->
        @if (fw.requiredToolboxVersion || fw.requiredToolVersion || fw.minOSFirmwareVersion) {
        <div>
            <h4 class="mt-0 mb-2">Requirements</h4>
            <div class="grid grid-cols-2 gap-2 text-sm">
                @if (fw.requiredToolboxVersion) {
                <div><span class="text-color-secondary">Toolbox:</span> {{ fw.requiredToolboxVersion }}</div>
                } @if (fw.requiredToolVersion) {
                <div><span class="text-color-secondary">Tool Version:</span> {{ fw.requiredToolVersion }}</div>
                } @if (fw.minOSFirmwareVersion) {
                <div><span class="text-color-secondary">Min OS:</span> {{ fw.minOSFirmwareVersion }}</div>
                }
            </div>
        </div>
        }

        <!-- Targets -->
        @if (fw.targets && (fw.targets | keyvalue)?.length) {
        <div>
            <h4 class="mt-0 mb-2">Targets</h4>
            <div class="flex flex-wrap gap-2">
                @for (target of fw.targets | keyvalue; track target.key) {
                <p-tag [value]="target.key + ': ' + target.value" severity="info" [style]="{fontSize: '0.75rem'}"></p-tag>
                }
            </div>
        </div>
        }

        <!-- Compatible Models -->
        @if (fw.compatibleModels.length) {
        <div>
            <h4 class="mt-0 mb-2">Compatible Models ({{ fw.compatibleModels.length }})</h4>
            <div class="flex flex-wrap gap-1">
                @for (model of fw.compatibleModels; track model) {
                <p-tag [value]="model" severity="secondary" [style]="{fontSize: '0.75rem'}"></p-tag>
                }
            </div>
        </div>
        }

        <!-- Release Notes -->
        @if (fw.releaseNotesUrl) {
        <div>
            <h4 class="mt-0 mb-2">Release Notes</h4>
            @if (fw.releaseNotesUrl.startsWith('http')) {
            <a [href]="fw.releaseNotesUrl" target="_blank" class="text-primary"> <i class="pi pi-external-link mr-1"></i>View Release Notes </a>
            } @else {
            <span class="text-color-secondary text-sm">{{ fw.releaseNotesUrl }}</span>
            }
        </div>
        }

        <!-- File hash -->
        <div>
            <span class="text-sm text-color-secondary">SHA256: </span>
            <code class="text-xs">{{ fw.fileHash }}</code>
        </div>

        <!-- Auto-Update URLs (public endpoints for Crestron auto-update scripts) -->
        @if (fw.compatibleModels.length) {
        <div>
            <h4 class="mt-0 mb-2">Auto-Update URLs</h4>
            <p class="text-sm text-color-secondary mt-0 mb-2">Public URLs for use with Crestron auto-update scripts. The hash URL returns a short text value that changes when a new version is uploaded.</p>
            <div class="mb-2">
                <p-select [ngModel]="selectedAutoUpdateModel()" (ngModelChange)="selectedAutoUpdateModel.set($event)" [options]="fw.compatibleModels" placeholder="Select model" class="w-full" appendTo="body"></p-select>
            </div>
            @if (selectedAutoUpdateModel(); as model) {
            <div class="p-2 rounded-border mb-2" style="background: var(--surface-ground)">
                <span class="text-xs font-semibold text-color-secondary">Latest (always points to newest version)</span>
                <div class="flex items-center gap-2 mb-1 mt-1">
                    <span class="text-xs text-color-secondary w-4rem">Download:</span>
                    <code class="text-xs flex-1 overflow-hidden text-overflow-ellipsis white-space-nowrap">{{ getPublicDownloadUrl(model, fw.firmwareType) }}</code>
                    <p-button icon="pi pi-copy" [text]="true" [rounded]="true" size="small" (onClick)="copyToClipboard(getPublicDownloadUrl(model, fw.firmwareType))" pTooltip="Copy URL"></p-button>
                </div>
                <div class="flex items-center gap-2">
                    <span class="text-xs text-color-secondary w-4rem">Hash:</span>
                    <code class="text-xs flex-1 overflow-hidden text-overflow-ellipsis white-space-nowrap">{{ getPublicHashUrl(model, fw.firmwareType) }}</code>
                    <p-button icon="pi pi-copy" [text]="true" [rounded]="true" size="small" (onClick)="copyToClipboard(getPublicHashUrl(model, fw.firmwareType))" pTooltip="Copy URL"></p-button>
                </div>
            </div>
            <div class="p-2 rounded-border" style="background: var(--surface-ground)">
                <span class="text-xs font-semibold text-color-secondary">Pinned to v{{ fw.version }}</span>
                <div class="flex items-center gap-2 mb-1 mt-1">
                    <span class="text-xs text-color-secondary w-4rem">Download:</span>
                    <code class="text-xs flex-1 overflow-hidden text-overflow-ellipsis white-space-nowrap">{{ getVersionedDownloadUrl(model, fw.originalFileName) }}</code>
                    <p-button icon="pi pi-copy" [text]="true" [rounded]="true" size="small" (onClick)="copyToClipboard(getVersionedDownloadUrl(model, fw.originalFileName))" pTooltip="Copy URL"></p-button>
                </div>
                <div class="flex items-center gap-2">
                    <span class="text-xs text-color-secondary w-4rem">Hash:</span>
                    <code class="text-xs flex-1 overflow-hidden text-overflow-ellipsis white-space-nowrap">{{ getVersionedHashUrl(model, fw.originalFileName) }}</code>
                    <p-button icon="pi pi-copy" [text]="true" [rounded]="true" size="small" (onClick)="copyToClipboard(getVersionedHashUrl(model, fw.originalFileName))" pTooltip="Copy URL"></p-button>
                </div>
            </div>
            }
        </div>
        }

        <!-- Actions -->
        <div class="flex gap-2 justify-end pt-2 border-top-1 surface-border">
            <p-button label="Download" icon="pi pi-download" (onClick)="downloadFirmware(fw)"></p-button>
            @if (canManageFirmware) {
            <p-button label="Delete" icon="pi pi-trash" severity="danger" [outlined]="true" (onClick)="confirmDelete(fw)"></p-button>
            }
        </div>
    </div>
    }
</p-dialog>

<!-- Upload Dialog -->
<p-dialog header="Upload Firmware" [(visible)]="showUploadDialog" [modal]="true" [draggable]="false" [resizable]="false" [closable]="!uploading()">
    <div class="p-3 flex items-center flex-col">
        <p class="text-color-secondary mb-4 text-center">Select a firmware file to upload (PUF or ZIP).<br />Metadata will be automatically extracted from the embedded .package.ini or ~info.ini file.</p>

        <div class="flex justify-center py-3">
            <p-fileupload
                mode="basic"
                [chooseLabel]="uploading() ? 'Uploading...' : 'Choose Firmware File'"
                accept=".puf,.zip"
                [auto]="true"
                (onSelect)="onFileSelect($event)"
                [chooseIcon]="uploading() ? 'pi pi-spin pi-spinner' : 'pi pi-upload'"
                [disabled]="uploading()"
                class="p-button-lg">
            </p-fileupload>
        </div>

        @if (uploading()) {
        <div class="upload-progress-panel flex flex-col items-center mt-4 w-full gap-3">
            <div class="w-full max-w-80">
                @if (uploadProgress() < 100) {
                <p-progressbar [value]="uploadProgress()" [showValue]="false" [style]="{height: '8px'}"></p-progressbar>
                } @else {
                <p-progressbar mode="indeterminate" [style]="{height: '8px'}"></p-progressbar>
                }
            </div>
            <div class="upload-status-text flex items-center gap-2 text-sm text-color-secondary">
                @if (uploadProgress() < 100) {
                <i class="pi pi-upload upload-icon"></i>
                <span>{{ uploadStatus() }} {{ uploadProgress() }}%</span>
                } @else {
                <i class="pi pi-spin pi-spinner processing-icon"></i>
                <span>{{ uploadStatus() }}</span>
                }
            </div>
        </div>
        }

        <p-message severity="info" class="mt-3 max-w-160 text-center">PUF files use .package.ini for metadata extraction. ZIP firmware files (NVX etc.) use ~info.ini for version and target device information. </p-message>
    </div>
</p-dialog>

<!-- Rename Dialog -->
<p-dialog header="Rename Firmware" [(visible)]="showRenameDialog" [modal]="true" [draggable]="false" [resizable]="false" [style]="{width: '400px', maxWidth: '95vw'}">
    <div class="flex flex-col gap-3 p-2">
        <label class="text-sm font-medium">Firmware Name</label>
        <input pInputText [ngModel]="renameValue()" (ngModelChange)="renameValue.set($event)" class="w-full" (keydown.enter)="submitRename()" />
    </div>
    <ng-template #footer>
        <p-button label="Cancel" [text]="true" (onClick)="showRenameDialog.set(false)" [disabled]="renaming()"></p-button>
        <p-button label="Rename" icon="pi pi-check" (onClick)="submitRename()" [loading]="renaming()" [disabled]="!renameValue().trim()"></p-button>
    </ng-template>
</p-dialog>

<!-- Confirm Dialog -->
<p-confirmdialog></p-confirmdialog>
